import { useEffect } from 'react'
import { z } from 'zod'
import { useBucket, useExperimentVariables } from '@mc/experiments-react'
import { reportError } from '@mc/next-newrelic'
import { trackExperimentViewed } from '@mc/track-event'
import { useSiteWidePromoVariables } from 'pages/certificates/promo'
import { EXPERIMENT_FLAG } from '../constants'

const MenuCopySchema = z.object({
  badgeText: z.string().min(1),
  title: z.string().min(1),
  subtitle: z.string().min(1),
})

export type CoreNavPromoMenuCopy = z.infer<typeof MenuCopySchema>

type CoreNavPromoSignal =
  | { enabled: false }
  | { enabled: true; menu: CoreNavPromoMenuCopy }

/**
 * Single entry point for the core-nav promo-signal experiment: resolves whether
 * to render the badge + promo card, and fires Experiment Viewed.
 *
 * Show when: bucketed `variant_1`, a live **site-wide** `certificates-promotions`
 * promo (the on/off gate, per PRD — treatment only during the promo window), AND
 * the variant carries valid `menu` copy. Copy (badge/title/subtitle) is authored
 * on the flag's variant_1 value (`menu`) and zod-validated, not in code — see
 * README; a malformed live variant_1 payload is reported. `isMenuOpen` gates
 * tracking so we only count real exposure; both arms are tracked on the same
 * condition (menu open + live promo) to keep control/variant balanced for SRM.
 */
export const useCoreNavPromoSignalExp = (
  isMenuOpen: boolean,
): CoreNavPromoSignal => {
  const bucket = useBucket<'control' | 'variant_1'>(EXPERIMENT_FLAG)
  const { menu } = useExperimentVariables<{ menu?: unknown }>(EXPERIMENT_FLAG)
  const promoVariables = useSiteWidePromoVariables()

  const isInExperiment = bucket === 'control' || bucket === 'variant_1'
  const promoActive = !!promoVariables

  useEffect(() => {
    if (isMenuOpen && promoActive && isInExperiment) {
      trackExperimentViewed(EXPERIMENT_FLAG)
    }
  }, [isMenuOpen, promoActive, isInExperiment])

  if (bucket === 'variant_1' && promoActive) {
    const validated = MenuCopySchema.safeParse(menu)
    if (validated.success) {
      return { enabled: true, menu: validated.data }
    }

    reportError(validated.error, {
      message: 'Invalid core-nav promo signal menu copy',
      experimentFlag: EXPERIMENT_FLAG,
      menu: JSON.stringify(menu),
    })
  }

  return { enabled: false }
}
